import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChildPickerEntry, ChildSessionUser, ParentSessionUser } from '@qq/shared';
import { ApiRequestError, api } from './api.js';

export interface SessionSnapshot {
  parent: ParentSessionUser | null;
  child: ChildSessionUser | null;
}

export const sessionKey = ['session'] as const;

/**
 * A child's photo is served by an endpoint that checks the session, not from a
 * static path. The timestamp busts the browser cache the moment the photo
 * changes, so a child never sees their old face after retaking it.
 */
export function childPhotoUrl(childId: string, updatedAt: string | null): string {
  const base = (import.meta.env?.VITE_API_BASE as string | undefined) ?? '/api';
  const stamp = updatedAt ? `?v=${encodeURIComponent(updatedAt)}` : '';
  return `${base}/children/${childId}/photo${stamp}`;
}

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: () => api<SessionSnapshot>('/auth/session'),
    staleTime: 30_000,
  });
}

export function useChildPicker() {
  return useQuery({
    queryKey: ['children', 'picker'],
    queryFn: () => api<ChildPickerEntry[]>('/children/picker'),
    // 401 means no grown-up has linked this device yet; retrying cannot fix that.
    retry: (count, err) => !(err instanceof ApiRequestError && err.status === 401) && count < 3,
  });
}

export function useChildLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { childId: string; pin: string }) =>
      api<ChildSessionUser>('/auth/child/login', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
}

export function useParentLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<ParentSessionUser>('/auth/parent/login', { method: 'POST', body: input }),
    // Signing in also links the device, so the picker can now answer.
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: sessionKey }),
        qc.invalidateQueries({ queryKey: ['children', 'picker'] }),
      ]),
  });
}

export function useLogout(kind: 'parent' | 'child') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<{ ok: true }>(`/auth/${kind}/logout`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
}
